import { useEffect, useState } from 'react'
import { Button, Card, CardFooter, Input, showSuccessToast } from '@bpinternal/ui-kit-next'
import { useUpdateBot } from '~/hooks'
import { validateConfigurationData } from '../../services'
import { BotObject } from '../../types'
import { Permission } from '../../../users/types'
import { trackEvent } from '../../../../providers'
import { EmptyState } from '~/elements'
import Variable from '~/assets/emptyStates/variable.svg?react'
import { Settings } from 'lucide-react'

type Props = {
  workspaceId: string
  bot: BotObject
  authorize: (permission: Permission) => boolean | undefined
}

export function BotConfigurationVariables({ bot, workspaceId, authorize }: Props): JSX.Element {
  const userCanEdit = authorize('bots.update')
  const [configurations, setConfigurations] = useState(validateConfigurationData(bot.configuration))
  const { mutate: updateBot, isPending } = useUpdateBot()
  useEffect(() => {
    setConfigurations(validateConfigurationData(bot.configuration))
  }, [bot.configuration])

  const updateBotConfigs = () => {
    updateBot({
      id: bot.id,
      workspaceId,
      configuration: { data: configurations },
      onSuccess: () => {
        showSuccessToast('The configuration variables have been updated successfully.')
      },
    })
  }
  const configurationKeys = Object.keys(bot.configuration.schema?.properties ?? {})

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <h2 className="font-semibold">Configuration variables</h2>
        <p className="text-xs text-neutral-500">Set up your bot's configuration variables</p>
      </div>
      <Card className="p-6">
        {configurationKeys.length > 0 ? (
          <>
            <div className="flex flex-col">
              {Object.entries(configurations).map(([configVarName, value]) => (
                <div key={configVarName} className="my-2">
                  <h2 className="mb-2 text-sm font-medium">{configVarName}</h2>
                  <Input
                    className="text-xs text-neutral-900"
                    onChange={(newValue) => {
                      setConfigurations((prev) => ({ ...prev, [configVarName]: newValue.target.value }))
                    }}
                    disabled={!userCanEdit}
                    value={value}
                  />
                </div>
              ))}
              {userCanEdit && (
                <CardFooter className="mt-4 flex justify-end p-0">
                  <Button
                    className="cursor-pointer"
                    disabled={isPending}
                    onClick={() => {
                      void updateBotConfigs()
                      trackEvent({ type: 'update_configuration_variables' })
                    }}
                  >
                    <Settings className="h-4 w-4" />
                    {isPending ? 'Saving...' : 'Save'}
                  </Button>
                </CardFooter>
              )}
            </div>
          </>
        ) : (
          <EmptyState
            title="No configuration variables found"
            description="Add configuration variables to your chatbot definition to set them here and use them in your bot's logic."
            iconSize={10}
            icon={Variable}
          />
        )}
      </Card>
    </div>
  )
}
